import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { LanguageContext } from '@/common/contexts/LanguageContext';
import { STRIPE_APPEARANCE } from '@/pages/customer/pawall/stripe/stripeAppearance';
import { buildStripePlanPaymentPayload } from '../helpers/buildStripePlanPaymentPayload';

export const usePlanPaymentIntent = ({ selectedPlan, user, createPayment }) => {
	const { lang } = useContext(LanguageContext);
	const [intent, setIntent] = useState(null);
	const [attempt, setAttempt] = useState(0);
	const [lastErrorMessage, setLastErrorMessage] = useState('');
	const requestKeyRef = useRef('');

	const createIntentMutation = useMutation({
		mutationFn: async (payload) => {
			const response = await createPayment(payload);

			if (!response.data?.clientSecret) {
				throw new Error('Missing Stripe client secret.');
			}

			return response;
		},
	});

	useEffect(() => {
		if (!selectedPlan || !user?.id) {
			requestKeyRef.current = '';
			setIntent(null);
			return;
		}

		const requestKey = `${selectedPlan.name}:${user.id}:${attempt}`;

		if (requestKeyRef.current === requestKey) {
			return;
		}

		requestKeyRef.current = requestKey;
		setIntent(null);

		createIntentMutation.mutate(
			buildStripePlanPaymentPayload({ planName: selectedPlan.name, user }),
			{
				onSuccess: ({ data }) => {
					if (requestKeyRef.current === requestKey) {
						setIntent(data);
					}
				},
			},
		);
	}, [selectedPlan?.name, user?.id, attempt]);

	const options = useMemo(() => {
		if (!intent?.clientSecret) {
			return null;
		}

		return {
			clientSecret: intent.clientSecret,
			locale: lang,
			appearance: STRIPE_APPEARANCE,
			loader: 'always',
		};
	}, [intent?.clientSecret, lang]);

	return {
		intent,
		options,
		isError: createIntentMutation.isError,
		lastErrorMessage,
		retry: (errorMessage = '') => {
			setLastErrorMessage(errorMessage);
			setAttempt((value) => value + 1);
		},
	};
};
